import { ZfInput, ZfLoadingSpinner, ZfSearchOption } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, useSearchPartnerByTerm } from '../../api';
import { CLIENT_SEARCH_MIN_LENGTH } from '../../config/constants';

interface IProps {
    onSetPartner: (partnerUuid: string) => void;
}

export const ClientSearchInput: FunctionComponent<IProps> = ({ onSetPartner }) => {
    const { t } = useTranslation();
    const { isLoading: isClientLoadingInProgress } = useClient();
    const inputRef = useRef<HTMLZfInputElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { data: partners, isLoading } = useSearchPartnerByTerm(searchTerm);

    const resetSearch = () => {
        setSearchTerm('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const options = useMemo(() => {
        if (partners?.length) return partners;
        if (searchTerm.length >= CLIENT_SEARCH_MIN_LENGTH) return [{ fullName: t('searchEmpty'), partnerUuid: '' }];

        return [];
    }, [partners, searchTerm.length, t]);

    if (isClientLoadingInProgress) return <ZfLoadingSpinner />;

    return (
        <ZfInput
            ref={inputRef}
            id="clientSearch"
            placeholder={t('clientSearchPlaceholder')}
            type="search"
            showSubmit={true}
            onInputChange={(e) => setSearchTerm(e.detail)}
        >
            {isLoading && <ZfLoadingSpinner slot="trailing" size="extra-small" color="primary" type="infinite" />}
            {options.map((partner) => (
                <ZfSearchOption
                    key={partner.partnerUuid}
                    description={partner.fullName}
                    descriptionHighlight={searchTerm}
                    onClick={() => (partner.partnerUuid ? onSetPartner(partner.partnerUuid) : resetSearch())}
                />
            ))}
        </ZfInput>
    );
};
